import type { TxStatus } from '@apm/contracts';

/**
 * 끝난 요청의 상태를 세 단계로 나눈다 (6회차 보완 결정 2, 7회차 결정 5).
 *
 * 저장하는 `status`는 OTel 규약대로 **5xx만** 오류로 둔다. 그래서 403 같은 4xx는 `ok`로 남고,
 * 그대로 그리면 "정상 (403)"에 "오류 종류 ForbiddenException"이 함께 떠 모순으로 보인다.
 * 계약은 그대로 두고 판정만 여기서 한다.
 *
 * DB 오류가 한 건이라도 있으면 최소 `fail`이다. 응답이 200이어도 요청 안에서 쿼리가 깨졌으면
 * 정상으로 보이면 안 된다.
 *
 * 13회차에 이 파일로 모았다. 그전에는 화면(`console/status.ts`)·검색(`query/range.ts`의
 * `toStatusSql`)·알림(`alert/evaluators.ts`)이 각자 판정했고, 검색과 알림은 `sqlErrorCount`를
 * 보지 않아 같은 요청이 화면에서는 '실패', 검색에서는 '정상'으로 나왔다.
 */

export const STATUS_LEVELS = ['ok', 'fail', 'error'] as const;
export type StatusLevel = (typeof STATUS_LEVELS)[number];

/** 판정에 필요한 것만. `TxSummary`도 그대로 들어온다 */
export interface StatusInput {
  status: TxStatus;
  httpStatus?: number;
  /** 요청 안에서 실패한 SQL 건수. 없는 옛 행은 0으로 본다 */
  sqlErrorCount?: number;
}

export function statusLevel(row: StatusInput): StatusLevel {
  if (row.status !== 'ok') return 'error';
  const http = row.httpStatus;
  if (http !== undefined && http >= 500) return 'error';
  if (http !== undefined && http >= 400) return 'fail';
  // 응답은 멀쩡한데 안에서 쿼리가 깨진 경우
  if ((row.sqlErrorCount ?? 0) > 0) return 'fail';
  return 'ok';
}

/**
 * 같은 판정을 DuckDB 조건으로 (13회차).
 *
 * `statusLevel`과 한 몸이라 여기 둔다. 세 조건은 서로 겹치지 않고 전체를 덮는다.
 * 컬럼 이름은 NDJSON·Parquet의 `TxSummary` 필드 그대로이고, 옛 행에는 `sqlErrorCount`가
 * 없을 수 있어 NULL을 0으로 본다.
 */
export function statusLevelSql(level: StatusLevel): string {
  if (level === 'error') return `("status" <> 'ok' OR "httpStatus" >= 500)`;
  const notError = `"status" = 'ok' AND ("httpStatus" IS NULL OR "httpStatus" < 500)`;
  const brokenSql = `COALESCE("sqlErrorCount", 0) > 0`;
  if (level === 'fail') {
    return `(${notError} AND (("httpStatus" >= 400 AND "httpStatus" < 500) OR ${brokenSql}))`;
  }
  return `(${notError} AND ("httpStatus" IS NULL OR "httpStatus" < 400) AND NOT ${brokenSql})`;
}
